import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Build a new design system in a couple afternoons",
  "description": "",
  "published": "2020-07-03T00:00:00.000Z",
  "redirect_from": ["/blog/build-a-new-design-system-in-a-couple-afternoons/swizec/9444"],
  "categories": "Front End, Technical",
  "hero": "./img/EL0L4QN.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Friend, my first design system took 3 years to build. My last just 3 days. Here's what I learned.`}</p>
    <p><img parentName="p" {...{
        "src": "/631444bbfa8daad4a1589775556ed2c9/qvxpdEY.gif",
        "alt": null
      }}></img></p>
    <p>{`First, what is a design system anyway?`}</p>
    <p>{`A design system is the ideal workflow for UI. A set of core reusable elements and rules that spit out good-looking interfaces without design skill on your part.`}</p>
    <p>{`You and your designer set the tone once, create the elements, some basic rules, then you work from mockups. Everything you build looks perfect and your designer spends time on UX not fiddly details.`}</p>
    <p>{`You the engineer can build perfect UI from a napkin sketch.`}</p>
    <p>{`😍`}</p>
    <p>{`That's the theory.`}</p>
    <p>{`Life is messy so a good design system must also be flexible. Local over-rides for one-offs.`}</p>
    <h2 {...{
      "id": "prereqs"
    }}>{`Prereqs`}</h2>
    <p>{`You'll need a couple prereqs before you get started:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`A designer that agrees systems are good`}</strong>{`. Some designers see their work as art. Every element is custom, every button a little different, every screen a new masterpiece. You can't systemize this. You will suffer. Sorry.`}</li>
      <li parentName="ol"><strong parentName="li">{`A componentized approach to UI`}</strong>{`. Use a component-based framework like React, Vue, or Angular. You `}<em parentName="li">{`can`}</em>{` do it with CSS classes but this is easier. Trust me.`}</li>
    </ol>
    <p>{`The main benefit of components is that you can encapsulate both UI `}<em parentName="p">{`and`}</em>{` UX. Not just looks but also behavior.`}</p>
    <p>{`PS: I got lucky and get to do this from scratch. You can introduce a design system gradually but it's a little trickier.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1276624374003404800"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "595px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "95.51569506726457%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'381\\'%20viewBox=\\'0%200%20400%20381\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M18%207C2%2015%208%2038%2026%2038c7%200%2015-5%208-6-5-1-3-7%202-7%207%200%207-10-1-16-5-4-11-5-17-2m-4%20130c-5%203-5%200-5%20106v100l2%203%203%204h77l1-4c2-4%201-5-1-1-2%202-2%202-2-4l2-24c2-44%2015-73%2031-73l6-1%204-1c2%201%206%208%206%2011l1%201v2l1%201c1-1%202%205%202%209l-1%209c-1%206-2%2010-2%206l-1-2c-1%200-2%203-1%207v4l-2%204-1%204-2%203-1%202h-1l1%201c1%201-2%207-4%208l-1%205v6l-1%201-1%201-1%202v2c1%201%200%203-2%209l-4%209v3h149l-2-2c-3-2-4-4-1-4l2%202%201%201h3l2%201h-1l-1%201%2022%201h22l-9-2c-30-7-30-7-28-8l41%207c14%203%2016%203%2037%203h23l1-7c1-6%202-7%203-7%202%200%203%201%203%204%201%206%203%208%205%205a929%20929%200%20001-116c0-14%200-17-1-15l-1%203-1-3-1-3-1%202-1%201h-1c-2%201%200-3%204-11l3-7v-54l-2-3-3-2-116-1H152v50c-1%2045-1%2052-2%2051a1027%201027%200%20010-95v-6H83l-69%201m165%2048c-3%204-3%209-1%2011%203%204%205%203%207-1l3-1%201-2v-2c1-1%200-2-1-3-3-3-7-4-9-2m-15%2049v7l5%2014%204-3%205-3-3-4-7-9c-3-5-3-5-4-2m24%2033l-4%201c-2%200-2%200-1%201v6c0%204-2%203-2-2l-2-4-2-1v6l4%2013c1%2010%205%2019%207%2019s5-3%205-5l1-2%201-1%203-3c2-2%202-3%202-9v-8c3-2%203-2-3-7-4-5-8-6-9-4m54%2017c-1%2015-1%2019-3%2027-1%203-1%204%201%205l8-2%203-1h2l-1-1v-3l1-3c3%200%206-4%205-5s-1-1%201-2c1-1%201-1-1-1-2%201-3-1%200-1%201-1%202-1%202-3l-1-3-2-3c-2-2-2-6%201-11%201-2-1-5-3-4-1%201-3%200-3-1l-2-2h-1l-4-1h-3v15\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/e7ec254a95a645e03edda8f0e27d4c0f/ca0a1/EL0L4QN.webp 223w", "/static/e7ec254a95a645e03edda8f0e27d4c0f/75680/EL0L4QN.webp 445w", "/static/e7ec254a95a645e03edda8f0e27d4c0f/536c8/EL0L4QN.webp 595w"],
              "sizes": "(max-width: 595px) 100vw, 595px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/e7ec254a95a645e03edda8f0e27d4c0f/e92b6/EL0L4QN.png 223w", "/static/e7ec254a95a645e03edda8f0e27d4c0f/e66bf/EL0L4QN.png 445w", "/static/e7ec254a95a645e03edda8f0e27d4c0f/3dd3e/EL0L4QN.png 595w"],
              "sizes": "(max-width: 595px) 100vw, 595px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/e7ec254a95a645e03edda8f0e27d4c0f/3dd3e/EL0L4QN.png",
              "alt": "EL0L4QN",
              "title": "EL0L4QN",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <h2 {...{
      "id": "goal"
    }}>{`Goal`}</h2>
    <p>{`Every select field, say, should look `}<em parentName="p">{`and`}</em>{` feel the same. Across your entire app. So why build it from scratch every time?`}</p>
    <p>{`You want something like this:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=import%20%7B%20Select%20%7D%20from%20%22my-design-system%22%0A%0Aconst%20Form%20%3D%20()%20%3D%3E%20%7B%0A%09return%20(%0A%09%09%3Cform%3E%0A%09%09%09%3CSelect%20options%3D%7B...%7D%20%2F%3E%0A%09%09%3C%2Fform%3E%0A%09)%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "430px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "76.68161434977578%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'307\\'%20viewBox=\\'0%200%20400%20307\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2046c-2%202-1%20215%201%20217s334%202%20336%200%203-215%201-217-336-2-338%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/e9d5cdbabb2eee3e3046a48488d02db6/ca0a1/techletter.app-screenshot-1593798386272.webp 223w", "/static/e9d5cdbabb2eee3e3046a48488d02db6/866c5/techletter.app-screenshot-1593798386272.webp 430w"],
                "sizes": "(max-width: 430px) 100vw, 430px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/e9d5cdbabb2eee3e3046a48488d02db6/e92b6/techletter.app-screenshot-1593798386272.png 223w", "/static/e9d5cdbabb2eee3e3046a48488d02db6/05ed2/techletter.app-screenshot-1593798386272.png 430w"],
                "sizes": "(max-width: 430px) 100vw, 430px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/e9d5cdbabb2eee3e3046a48488d02db6/05ed2/techletter.app-screenshot-1593798386272.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`And that's your standard beautiful magnificent and all round marvelous select field.`}</p>
    <p>{`Build it once use it everywhere. ✌️`}</p>
    <p>{`Test it once, approve it once, back-and-forth with designer once, back-and-forth with QA once, back-and-forth with PM once ... and then it works forever. You're good until the next redesign.`}</p>
    <p>{`And with a little luck any future redesign will touch just your design system. You might not even have to touch any application code.`}</p>
    <p><img parentName="p" {...{
        "src": "/2628a55f38c158a55358af3bb9b7ae30/media-J8Iqja8CXfMw8-giphy.gif",
        "alt": "the_dream giphy"
      }}></img></p>
    <p>{`Where classes – utility classes especially – fall down is this reusability. Do I have to add `}<inlineCode parentName="p">{`bg-gray flex1 color-blue p3`}</inlineCode>{` or was it `}<inlineCode parentName="p">{`bg-gray flex2 color-white p4`}</inlineCode>{` 🤔`}</p>
    <p>{`Stop. You've already lost.`}</p>
    <p>{`Every QA cycle, every PM test, every new feature you will have to carefully vet every little detail. Because they're always done from scratch.`}</p>
    <p>{`A design system saves you that effort.`}</p>
    <h2 {...{
      "id": "how-you-create-a-design-system-in-2020"
    }}>{`How you create a design system in 2020`}</h2>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1277776081559289856"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "597px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "132.28699551569508%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'530\\'%20viewBox=\\'0%200%20400%20530\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M22%206c-11%204-14%2017-7%2026l4%204v-2l-3-5c-1-4-1-4%202-4l2%202-1%201v1c1%202%207%202%208%200l2-1-1-1v-2l3-1c2%200%202%200%200%204-2%206-4%208-8%208-3%200-3%200-1%201%203%201%209%201%2013-1s5-4%202-4l-4-1c-1-2%201-6%205-6l2-2%202-1h1c1-5-2-11-6-12l-2-2-8-3-5%201m-9%20188c-2%203%201%209%205%2011h6c4-2%207-8%205-11-1-4-14-4-16%200m248%200c-2%203%201%209%205%2011%206%203%2014-6%2011-11-1-4-15-4-16%200M16%20218c-5%202-5-2-5%20143l1%20136%202%203%203%203h114l2-5c1-3%202-4%204-4l2-1-5-5c-2%200-5-6-5-8%202-7%205-12%209-18%208-8%208-9%208-24%201-23%205-29%2016-24%204%202%205%202%206%200%204-2%2011-1%2014%203l4%201%203%201v-6l1-13-5%202-5%203-6-3-7-3-4-3c-2-3-5-4-9-5-12-3-14-4-17-9l-2-7-1-8c-5-16%200-29%2012-36l11-8c9-8%208-8%2019-4%2011%205%2018%205%2017%201-1-1%201-3%202-1h4c3%200%203%200%203%204l-1%205c-2%200-1%203%201%206l1%203%201%201%201%201%202%204%202%203-1-7-1-10c-1-5-1-5%201-5l1%202c0%203%203%201%204-3%201-5%200-7-2-5-1%202-3%202-5%200-3-1-2-1%203-7l2-5v-2l2%202c6%205%2011%207%2024%208%2011%201%2017%202%2017%204l3%202c4%202%209%208%2013%2015l8%208%205%206c0%203%205%207%207%206l6%203%209%203%208%201c2%200%204%200%205-2h9c9%201%2010%201%2010-1s1-3%207-3c4-1%207-1%208-3%203-3%206-3%208%201%203%204%202%205-1%208l-3%205-2%204-3%205c-1%207-12%2019-18%2020l-5%201-11%201-11%201-6%201-9%201c-10%206-19%206-27%203-3-2-4%200-4%209a311%20311%200%20007%2059c2%205%202%206%202%2011-1%206%201%2018%202%2019%202%202%20112%201%20115%200%205-3%205%205%205-142%200-148%200-140-5-143-5-2-367-1-370%201m201%20101v13l3%2012%204%2020%201%2013%201%2022a1162%201162%200%2000-1%2031c-2-1-2-1-3%202s-1%203-12%202c-2-1-2%203%200%209%200%203%200%203-3%205-6%202-7%202-7-2%201-5%202-8%202-22v-12l2%203%202%204v-6c0-5%200-7-2-8l-1-5c0-4%200-4%202-4l4%202c3%201%203-2-1-6-2-2-3-2-5-2-2%201-2%201-3%2014a247%20247%200%2001-3%2035l-1-1c-2%200-3%206-3%2016%200%208%200%208%204%2011s11%204%2023%203c9-1%2011-2%2011-6l1-2%201%202c0%202%200%202%202%202%206-2%209-2%2010-1%202%201%203%200%204-3l2-2c1%201%205%2014%205%2018l1%204%201%203c0%203%202%206%204%206s0-19-3-26l-2-5-2-7-3-8-1-4-1-9c-1-8-3-14-4-11-1%201-1%200-1-1l1-3v-20c-3-25-4-27-4-7a4506%204506%200%20012%2051l2%204c2%204%201%204-5%204s-12%201-17%203l-6-4-5-5%202-2%2012-1%209%201-1-3c-1-2-2-3-1-4%201-2%200-35-2-42l2-4v-7l-2-13c-1-8-3-15-5-15-1%200-6-16-5-17%202%200%201-5-2-10l-3-5m14%201l2%203%203%206%202%204c2%203%207%2020%208%2029a752%20752%200%20014%2042l1-26c-1-27-1-29-8-43-4-9-12-19-12-15m-32%2026c-2%202-1%205%201%205s6%205%207%2010l3-1%202-1c2%200%204%208%203%2010v2l-1-2c-1-3-7-2-6%201v7c0%205%201%206%206%206%203%200%203%200%203%203l1%205c1%201%201-2%201-9a60%2060%200%2000-6-30l-1-4c-2-1-2-1-1%203v4l-2-3-2-3c0-3-6-5-8-3m21%2060c0%203-1%204-4%202-5-3-10%203-7%207%202%201%202%202%201%202l-1%202%203-1c1-2%204-2%207%201%202%202%206%202%206%201%201-1%200-3-1-5l-2-4v-5l-2-3v3m-39%2020c-2%208-2%2024-1%2022%202-2%204-1%205%201l1-7%202-13c0-5%200-5-2-5h-4l-1%202m-2%2038c0%2010%201%2010%202%2010l4%201c1%201%201%200%201-10s-1-10-2-10l-4-1c-1-1-1%201-1%2010m6%2018l-3%201c-2-1-2%200-2%209v10h7v-9c0-9-1-13-2-11\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/8299f4e6893e241c219545a771c05bd5/ca0a1/fEe1Ucy.webp 223w", "/static/8299f4e6893e241c219545a771c05bd5/75680/fEe1Ucy.webp 445w", "/static/8299f4e6893e241c219545a771c05bd5/9e35d/fEe1Ucy.webp 597w"],
              "sizes": "(max-width: 597px) 100vw, 597px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/8299f4e6893e241c219545a771c05bd5/e92b6/fEe1Ucy.png 223w", "/static/8299f4e6893e241c219545a771c05bd5/e66bf/fEe1Ucy.png 445w", "/static/8299f4e6893e241c219545a771c05bd5/17602/fEe1Ucy.png 597w"],
              "sizes": "(max-width: 597px) 100vw, 597px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/8299f4e6893e241c219545a771c05bd5/17602/fEe1Ucy.png",
              "alt": "fEe1Ucy",
              "title": "fEe1Ucy",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <p>{`3 years ago when I started building my first, the ecosystem wasn't quite "there" yet.`}</p>
    <p>{`CSS-in-JS or CSS modules? Big fat CSS classes or utilities? React, Vue, or nothing? How to scales? How to colors? Weird names or long names? Build a catalogue or not? How do you docs? Do you even need docs?`}</p>
    <p>{`Crap, what about responsiveness? Do you grid or flex? `}<inlineCode parentName="p">{`float: left`}</inlineCode>{`?? 🤨`}</p>
    <p>{`I've tried it all.`}</p>
    <p>{`This is what I've landed on as the ultimate solution:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Use `}<a parentName="strong" {...{
            "href": "https://github.com/storybookjs/storybook"
          }}>{`Storybook`}</a></strong>{` – spits out a catalogue and docs too. Live preview of your components, human readable descriptions with docs, you can even just write documentation in MDX and storybook handles the rest. Hell it even spits out API Docs based on TypeScript types. 🤘`}</li>
      <li parentName="ol"><strong parentName="li">{`Add `}<a parentName="strong" {...{
            "href": "https://www.chromatic.com/"
          }}>{`Chromatic`}</a></strong>{` – a SaaS from the creators of Storybook. Gives you a hosted versioned Storybook. Added bonus: snapshot testing of all your components. Screams at you when there's a visual regression 😍`}</li>
      <li parentName="ol"><strong parentName="li">{`Use TypeScript`}</strong>{` – the VS Code experience with TS is just wonderful. Hints and docs as you mouse-over a component are fantastic. Very helpful when you work on a team.`}</li>
      <li parentName="ol"><strong parentName="li">{`Start with `}<a parentName="strong" {...{
            "href": "https://github.com/system-ui/theme-ui"
          }}>{`theme-ui`}</a></strong>{` – this part is React only. `}<inlineCode parentName="li">{`theme-ui`}</inlineCode>{` is the best balance between developer experience/ergonomics and solid design system. Does most of the work for you.`}</li>
    </ol>
    <p>{`I started from scratch with `}<inlineCode parentName="p">{`npm install @storybook/react --save-dev`}</inlineCode>{` and 3 days later had a full custom design system ✌️`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1278500671587663872"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "595px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "127.35426008968611%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'510\\'%20viewBox=\\'0%200%20400%20510\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M18%208c-6%204-8%207-7%2014%200%2014%2014%2022%2025%2014l3-2-3-2-4-2c0-1%203-4%205-4l3-1%202-1v-8C38%207%2025%203%2018%208m74%20352v15H11v9h40l41%201v111h84V354h124v142h83V346H92v14m95%2017l-2%205c0%207-1%207%2053%207h49l2-3c3-3%203-5%200-8l-2-3h-49c-47%200-50%200-51%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/710e799a0cb41c5eba9cd0e51e830af3/ca0a1/I5390QO.webp 223w", "/static/710e799a0cb41c5eba9cd0e51e830af3/75680/I5390QO.webp 445w", "/static/710e799a0cb41c5eba9cd0e51e830af3/536c8/I5390QO.webp 595w"],
              "sizes": "(max-width: 595px) 100vw, 595px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/710e799a0cb41c5eba9cd0e51e830af3/e92b6/I5390QO.png 223w", "/static/710e799a0cb41c5eba9cd0e51e830af3/e66bf/I5390QO.png 445w", "/static/710e799a0cb41c5eba9cd0e51e830af3/3dd3e/I5390QO.png 595w"],
              "sizes": "(max-width: 595px) 100vw, 595px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/710e799a0cb41c5eba9cd0e51e830af3/3dd3e/I5390QO.png",
              "alt": "I5390QO",
              "title": "I5390QO",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <p>{`The trick is to lean hard into the ecosystem and let others do your work.`}</p>
    <h3 {...{
      "id": "heres-how"
    }}>{`Here's how`}</h3>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Install all the dependencies`}</strong></li>
    </ol>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=null&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20devDependencies%0A%22%40babel%2Fcore%22%3A%20%22%5E7.10.3%22%2C%0A%22%40storybook%2Faddon-info%22%3A%20%22%5E5.3.19%22%2C%0A%22%40storybook%2Fpreset-typescript%22%3A%20%22%5E3.0.0%22%2C%0A%22%40storybook%2Freact%22%3A%20%22%5E5.3.19%22%2C%0A%22%40theme-ui%2Fpresets%22%3A%20%22%5E0.3.0%22%2C%0A%22%40types%2Fjest%22%3A%20%22%5E26.0.3%22%2C%0A%22%40types%2Fnode%22%3A%20%22%5E14.0.14%22%2C%0A%22%40types%2Freact%22%3A%20%22%5E16.9.41%22%2C%0A%22%40types%2Freact-dom%22%3A%20%22%5E16.9.8%22%2C%0A%22%40types%2Fstorybook__addon-info%22%3A%20%22%5E5.2.1%22%2C%0A%22%40types%2Ftheme-ui%22%3A%20%22%5E0.3.4%22%2C%0A%22babel-loader%22%3A%20%22%5E8.1.0%22%2C%0A%22chromatic%22%3A%20%22%5E5.0.0%22%2C%0A%22jest%22%3A%20%22%5E26.1.0%22%2C%0A%22react-docgen-typescript-loader%22%3A%20%22%5E3.7.2%22%2C%0A%22ts-jest%22%3A%20%22%5E26.1.1%22%2C%0A%22ts-loader%22%3A%20%22%5E7.0.5%22%2C%0A%22typescript%22%3A%20%22%5E3.9.5%22%0A%22storybook-addon-theme-ui%22%3A%20%22%5E0.1.5%22%2C%0A%0A%2F%2F%20peer%20dependencies%0A%22react-dom%22%3A%20%22%5E16.13.1%22%2C%0A%22react%22%3A%20%22%5E16.13.1%22%0A%0A%2F%2F%20dependencies%0A%22theme-ui%22%3A%20%22%5E0.3.1%22"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "446px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "146.63677130044843%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'587\\'%20viewBox=\\'0%200%20400%20587\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2044c-2%201-2%2014-2%20251v249l3%201c4%202%20337%201%20338-1l1-250c0-245%200-249-2-250H31\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9422b21543fb86af53f460c6f95d2caf/ca0a1/techletter.app-screenshot-1593798386174.webp 223w", "/static/9422b21543fb86af53f460c6f95d2caf/75680/techletter.app-screenshot-1593798386174.webp 445w", "/static/9422b21543fb86af53f460c6f95d2caf/5021b/techletter.app-screenshot-1593798386174.webp 446w"],
                "sizes": "(max-width: 446px) 100vw, 446px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9422b21543fb86af53f460c6f95d2caf/e92b6/techletter.app-screenshot-1593798386174.png 223w", "/static/9422b21543fb86af53f460c6f95d2caf/e66bf/techletter.app-screenshot-1593798386174.png 445w", "/static/9422b21543fb86af53f460c6f95d2caf/6244b/techletter.app-screenshot-1593798386174.png 446w"],
                "sizes": "(max-width: 446px) 100vw, 446px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/9422b21543fb86af53f460c6f95d2caf/6244b/techletter.app-screenshot-1593798386174.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p><inlineCode parentName="p">{`theme-ui`}</inlineCode>{` is your only real dependency. The rest are used in development.`}</p>
    <p>{`Don't worry about the low version number on `}<inlineCode parentName="p">{`theme-ui`}</inlineCode>{`. It builds on lessons learned from several earlier attempts.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/jxnblk"
      }}>{`@jxnblk`}</a>{` did a great job`}</p>
    <ol {...{
      "start": 2
    }}>
      <li parentName="ol"><strong parentName="li">{`Configure storybook to be fab`}</strong></li>
    </ol>
    <p>{`Storybook by default is great. Storybook with some addons is fantastic.`}</p>
    <p>{`The most important parts for me are:`}</p>
    <ul>
      <li parentName="ul">{`easy/automatic documentation`}</li>
      <li parentName="ul">{`accessibility checks`}</li>
      <li parentName="ul">{`knobs to visually test components`}</li>
      <li parentName="ul">{`typescript support`}</li>
    </ul>
    <p>{`Configuring all this happens in the `}<inlineCode parentName="p">{`.storybook`}</inlineCode>{` directory.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20.storybook%2Faddons.js%0A%0A%2F%2F%20register%20some%20of%20the%20addons%0Aimport%20%22%40storybook%2Faddon-actions%2Fregister%22%3B%0Aimport%20%22%40storybook%2Faddon-viewport%2Fregister%22%3B%0Aimport%20%22%40storybook%2Faddon-a11y%2Fregister%22%3B"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "454px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "60.98654708520179%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'243\\'%20viewBox=\\'0%200%20400%20243\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M30%2044c-2%201-2%208-2%2079v77l2%201c4%203%20336%203%20340%200l2-1v-77c0-74%200-78-2-79-3-3-338-3-340%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d741c2afe75ed6a1a325738982f49bab/ca0a1/techletter.app-screenshot-1593798387781.webp 223w", "/static/d741c2afe75ed6a1a325738982f49bab/75680/techletter.app-screenshot-1593798387781.webp 445w", "/static/d741c2afe75ed6a1a325738982f49bab/44d9f/techletter.app-screenshot-1593798387781.webp 454w"],
                "sizes": "(max-width: 454px) 100vw, 454px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d741c2afe75ed6a1a325738982f49bab/e92b6/techletter.app-screenshot-1593798387781.png 223w", "/static/d741c2afe75ed6a1a325738982f49bab/e66bf/techletter.app-screenshot-1593798387781.png 445w", "/static/d741c2afe75ed6a1a325738982f49bab/b3c1d/techletter.app-screenshot-1593798387781.png 454w"],
                "sizes": "(max-width: 454px) 100vw, 454px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d741c2afe75ed6a1a325738982f49bab/b3c1d/techletter.app-screenshot-1593798387781.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Some addons like to be included via the `}<inlineCode parentName="p">{`addons.js`}</inlineCode>{` file. Others go in `}<inlineCode parentName="p">{`main.js`}</inlineCode>{`. I still don't understand why.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20.storybook%2Fconfig.js%0A%0A%2F%2F%20configure%20defaults%20for%20all%20your%20stories%0A%0Aimport%20%7B%20addParameters%2C%20addDecorator%20%7D%20from%20%22%40storybook%2Freact%22%3B%0Aimport%20%22%40storybook%2Faddon-console%22%3B%0Aimport%20%7B%20withThemeProvider%20%7D%20from%20%22storybook-addon-theme-ui%22%3B%0Aimport%20%7B%20withConsole%20%7D%20from%20%22%40storybook%2Faddon-console%22%3B%0Aimport%20%7B%20withA11y%20%7D%20from%20%22%40storybook%2Faddon-a11y%22%3B%0Aimport%20%7B%20withKnobs%20%7D%20from%20%22%40storybook%2Faddon-knobs%22%3B%0Aimport%20*%20as%20presets%20from%20%22%40theme-ui%2Fpresets%22%3B%0A%0Aimport%20*%20as%20themes%20from%20%22..%2Fsrc%2Fthemes%22%3B%0A%0AaddParameters(%7B%0A%20%20%20%20themeUi%3A%20%7B%0A%20%20%20%20%20%20%20%20themes%3A%20%5B%0A%20%20%20%20%20%20%20%20%20%20%20%20%7B%20theme%3A%20themes.askTia%2C%20name%3A%20%22Ask%20Tia%22%20%7D%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%7B%20theme%3A%20presets.base%2C%20name%3A%20%22Base%20Preset%22%20%7D%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%7B%20theme%3A%20presets.deep%2C%20name%3A%20%22Deep%20Preset%22%20%7D%2C%0A%20%20%20%20%20%20%20%20%5D%2C%0A%20%20%20%20%7D%2C%0A%20%20%20%20actions%3A%20%7B%20argTypesRegex%3A%20%22%5Eon.*%22%20%7D%2C%0A%7D)%3B%0A%0AaddDecorator((storyFn%2C%20context)%20%3D%3E%20withConsole()(storyFn)(context))%3B%0AaddDecorator(withThemeProvider)%3B%0AaddDecorator(withA11y)%3B%0AaddDecorator(withKnobs)%3B"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "646px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "106.72645739910314%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'427\\'%20viewBox=\\'0%200%20400%20427\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M21%2031c-2%201-1%20365%200%20367%202%202%20356%202%20358%200l1-185V31l-180-1-179%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f28b43609b79379ed662710206374a34/ca0a1/techletter.app-screenshot-1593798388118.webp 223w", "/static/f28b43609b79379ed662710206374a34/75680/techletter.app-screenshot-1593798388118.webp 445w", "/static/f28b43609b79379ed662710206374a34/8c2f2/techletter.app-screenshot-1593798388118.webp 646w"],
                "sizes": "(max-width: 646px) 100vw, 646px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f28b43609b79379ed662710206374a34/e92b6/techletter.app-screenshot-1593798388118.png 223w", "/static/f28b43609b79379ed662710206374a34/e66bf/techletter.app-screenshot-1593798388118.png 445w", "/static/f28b43609b79379ed662710206374a34/27524/techletter.app-screenshot-1593798388118.png 646w"],
                "sizes": "(max-width: 646px) 100vw, 646px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/f28b43609b79379ed662710206374a34/27524/techletter.app-screenshot-1593798388118.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`This configures defaults for every story in your storybook. That is every example you want to put up. Things like adding the theme provider, enabling knobs and accessibility checks, etc.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20.storybook%2Fmain.js%0A%0A%2F%2F%20configure%20webpack%2C%20more%20addons%2C%20and%20typescript%0Aconst%20path%20%3D%20require(%22path%22)%3B%0A%0Amodule.exports%20%3D%20%7B%0A%20%20%20%20stories%3A%20%5B%22..%2Fsrc%2F**%2F*.stories.%40(tsx%7Cjs%7Cmdx)%22%5D%2C%0A%20%20%20%20addons%3A%20%5B%0A%20%20%20%20%20%20%20%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20name%3A%20%22%40storybook%2Faddon-docs%22%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20options%3A%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20configureJSX%3A%20true%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20babelOptions%3A%20%7B%7D%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20sourceLoaderOptions%3A%20null%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%7D%2C%0A%20%20%20%20%20%20%20%20%7D%2C%0A%20%20%20%20%20%20%20%20%22%40storybook%2Faddon-knobs%2Fregister%22%2C%0A%20%20%20%20%5D%2C%0A%20%20%20%20webpackFinal%3A%20async%20(config)%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20config.module.rules.push(%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20test%3A%20%2F%5C.(ts%7Ctsx)%24%2F%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20use%3A%20%5B%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20loader%3A%20require.resolve(%22ts-loader%22)%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%7D%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%2F%20Optional%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20loader%3A%20require.resolve(%22react-docgen-typescript-loader%22)%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%7D%2C%0A%20%20%20%20%20%20%20%20%20%20%20%20%5D%2C%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%0A%20%20%20%20%20%20%20%20config.resolve.extensions.push(%22.ts%22%2C%20%22.tsx%22)%3B%0A%20%20%20%20%20%20%20%20return%20config%3B%0A%20%20%20%20%7D%2C%0A%7D%3B"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "726px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "112.55605381165918%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'450\\'%20viewBox=\\'0%200%20400%20450\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M19%2028a14886%2014886%200%20001%20396h361l1-199V28l-182-1-181%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/902efbdecc60adfa12feab62e3ee2b2b/ca0a1/techletter.app-screenshot-1593798386297.webp 223w", "/static/902efbdecc60adfa12feab62e3ee2b2b/75680/techletter.app-screenshot-1593798386297.webp 445w", "/static/902efbdecc60adfa12feab62e3ee2b2b/06b0e/techletter.app-screenshot-1593798386297.webp 726w"],
                "sizes": "(max-width: 726px) 100vw, 726px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/902efbdecc60adfa12feab62e3ee2b2b/e92b6/techletter.app-screenshot-1593798386297.png 223w", "/static/902efbdecc60adfa12feab62e3ee2b2b/e66bf/techletter.app-screenshot-1593798386297.png 445w", "/static/902efbdecc60adfa12feab62e3ee2b2b/f8067/techletter.app-screenshot-1593798386297.png 726w"],
                "sizes": "(max-width: 726px) 100vw, 726px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/902efbdecc60adfa12feab62e3ee2b2b/f8067/techletter.app-screenshot-1593798386297.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`This part adds the documentation addon, support for typescript, and MDX.`}</p>
    <p>{`MDX is important because it means you write your design system descriptions `}<em parentName="p">{`once`}</em>{` and they double as both testing and documentation. It's great.`}</p>
    <ol {...{
      "start": 3
    }}>
      <li parentName="ol"><strong parentName="li">{`Customize theme-ui for your design`}</strong></li>
    </ol>
    <p>{`This is the fun part.`}</p>
    <p><inlineCode parentName="p">{`theme-ui`}</inlineCode>{` actually does all the work for you. As long as you don't need custom behavior.`}</p>
    <p>{`It comes with more pre-built components than you can shake a stick at. All the components you'll need, really.`}</p>
    <p>{`And it supports themes for custom coloring, spacing, etc. All `}<em parentName="p">{`you`}</em>{` gotta do is configure a custom theme for your organization.`}</p>
    <p>{`Something like this:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20src%2Fthemes.ts%0A%0Aexport%20const%20askTia%20%3D%20%7B%0A%20%20%20%20space%3A%20%5B0%2C%204%2C%208%2C%2016%2C%2032%2C%2048%2C%2064%2C%20128%2C%20256%2C%20512%5D%2C%0A%20%20%20%20sizes%3A%20%5B0%2C%204%2C%208%2C%2016%2C%2032%2C%2048%2C%2064%2C%20128%2C%20256%2C%20512%5D%2C%0A%20%20%20%20radiuses%3A%20%5B0%2C%205%2C%2010%5D%2C%0A%20%20%20%20fonts%3A%20%7B%0A%20%20%20%20%20%20%20%20body%3A%20%22GT_WalsheimRegular%2C%20Helvetica%2C%20sans-serif%22%2C%0A%20%20%20%20%20%20%20%20heading%3A%20%22inherit%22%2C%0A%20%20%20%20%20%20%20%20monospace%3A%20%22Menlo%2C%20monospace%22%0A%20%20%20%20%7D%2C%0A%20%20%20%20fontSizes%3A%20%5B14%2C%2016%2C%2018%2C%2022%5D%2C%0A%20%20%20%20%2F%2F%20...%0A%20%20%20%20colors%3A%20%7B%0A%20%20%20%20%20%20%20%20...tiaColors%2C%0A%0A%20%20%20%20%20%20%20%20%2F%2F%20colors%20required%20by%20theme-spec%0A%20%20%20%20%20%20%20%20%2F%2F%20https%3A%2F%2Ftheme-ui.com%2Ftheme-spec%0A%20%20%20%20%20%20%20%20text%3A%20tiaColors.black%2C%0A%20%20%20%20%20%20%20%20background%3A%20tiaColors.white%2C%0A%20%20%20%20%20%20%20%20primary%3A%20tiaColors.pink%2C%0A%20%20%20%20%20%20%20%20secondary%3A%20tiaColors.blue%2C%0A%20%20%20%20%20%20%20%20accent%3A%20tiaColors.teal%2C%0A%20%20%20%20%20%20%20%20highlight%3A%20tiaColors.pink%2C%0A%20%20%20%20%20%20%20%20muted%3A%20tiaColors.whisper%0A%20%20%20%20%7D%2C"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "566px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "112.55605381165918%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'449\\'%20viewBox=\\'0%200%20400%20449\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M23%2036a12902%2012902%200%20001%20380l176%201%20176-1a12902%2012902%200%20001-380c-1-3-353-3-354%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/df1058aeacec86c75e1634bf58fb08af/ca0a1/techletter.app-screenshot-1593798386275.webp 223w", "/static/df1058aeacec86c75e1634bf58fb08af/75680/techletter.app-screenshot-1593798386275.webp 445w", "/static/df1058aeacec86c75e1634bf58fb08af/3161c/techletter.app-screenshot-1593798386275.webp 566w"],
                "sizes": "(max-width: 566px) 100vw, 566px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/df1058aeacec86c75e1634bf58fb08af/e92b6/techletter.app-screenshot-1593798386275.png 223w", "/static/df1058aeacec86c75e1634bf58fb08af/e66bf/techletter.app-screenshot-1593798386275.png 445w", "/static/df1058aeacec86c75e1634bf58fb08af/6fe44/techletter.app-screenshot-1593798386275.png 566w"],
                "sizes": "(max-width: 566px) 100vw, 566px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/df1058aeacec86c75e1634bf58fb08af/6fe44/techletter.app-screenshot-1593798386275.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Custom colors are the quickest way to achieve a great custom look.`}</p>
    <p>{`I recommend giving them color names. Primary/secondary/accent gets confusing fast. Especially when variants are in play.`}</p>
    <p>{`You should map primary to color so `}<inlineCode parentName="p">{`theme-ui`}</inlineCode>{` default components pick up on your colors. This saves you time.`}</p>
    <ol {...{
      "start": 4
    }}>
      <li parentName="ol"><strong parentName="li">{`Customize theme-ui elements`}</strong></li>
    </ol>
    <p>{`Once the basic scales are in place, you gotta apply them to different elements. Rather than creating your own from scratch, tell `}<inlineCode parentName="p">{`theme-ui`}</inlineCode>{` what to do.`}</p>
    <p>{`A custom input, for example.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20src%2Fthemes.ts%0A%0Aconst%20tiaInput%20%3D%20%7B%0A%20%20%20%20borderColor%3A%20%22greyVeryLight%22%2C%0A%20%20%20%20pt%3A%200%2C%0A%20%20%20%20pr%3A%203%2C%0A%20%20%20%20pb%3A%200%2C%0A%20%20%20%20pl%3A%203%2C%0A%20%20%20%20height%3A%205%2C%0A%20%20%20%20color%3A%20%22text%22%2C%0A%20%20%20%20fontSize%3A%201%2C%0A%20%20%20%20fontFamily%3A%20%22body%22%2C%0A%20%20%20%20mb%3A%202%2C%0A%20%20%20%20borderRadius%3A%20(t%3A%20any)%20%3D%3E%20t.radiuses%5B2%5D%2C%0A%20%20%20%20%22%26%3Afocus%22%3A%20%7B%0A%20%20%20%20%20%20%20%20borderColor%3A%20%22pink%22%2C%0A%20%20%20%20%20%20%20%20outline%3A%20%22none%22%0A%20%20%20%20%7D%0A%7D%3B%0A%0A%2F%2F%20...%0A%0Aexport%20const%20askTia%20%3D%20%7B%0A%09%2F%2F%20...%0A%09forms%3A%20%7B%0A%20%20%20%20input%3A%20%7B%0A%20%20%20%20%20%20%20%20...tiaInput%0A%20%20%20%20%7D%2C%0A%20%20%20%20select%3A%20%7B%0A%20%20%20%20%20%20%20%20...tiaInput%2C%0A%20%20%20%20%20%20%20%20%2F%2F%20this%20is%20to%20fix%20caret%20centering%0A%20%20%20%20%20%20%20%20%22%26%20%2B%20svg%22%3A%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20%22margin-top%22%3A%20%22-10px%22%0A%20%20%20%20%20%20%20%20%7D%0A%20%20%20%20%7D%2C%0A%20%20%20%20%2F%2F%20...%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "454px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "183.85650224215246%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'735\\'%20viewBox=\\'0%200%20400%20735\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M30%2044l-2%201v323c0%20300%200%20324%202%20325%202%202%20338%202%20340%200%202-1%202-25%202-325V45l-2-1c-4-3-336-3-340%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/98eeaf673a7f67d4ae80f29e57e82969/ca0a1/techletter.app-screenshot-1593798385578.webp 223w", "/static/98eeaf673a7f67d4ae80f29e57e82969/75680/techletter.app-screenshot-1593798385578.webp 445w", "/static/98eeaf673a7f67d4ae80f29e57e82969/44d9f/techletter.app-screenshot-1593798385578.webp 454w"],
                "sizes": "(max-width: 454px) 100vw, 454px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/98eeaf673a7f67d4ae80f29e57e82969/e92b6/techletter.app-screenshot-1593798385578.png 223w", "/static/98eeaf673a7f67d4ae80f29e57e82969/e66bf/techletter.app-screenshot-1593798385578.png 445w", "/static/98eeaf673a7f67d4ae80f29e57e82969/b3c1d/techletter.app-screenshot-1593798385578.png 454w"],
                "sizes": "(max-width: 454px) 100vw, 454px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/98eeaf673a7f67d4ae80f29e57e82969/b3c1d/techletter.app-screenshot-1593798385578.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`And a custom form falls out ❤️`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "548px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/7c778a283cc601e6d43719ebef9fb98d/a58fe/KySCLMN.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "91.03139013452915%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'365\\'%20viewBox=\\'0%200%20400%20365\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M14%2033h1l183-1h181l3%203c2%202%202%202%202%2015v12l1-13c0-13%200-13-2-16l-3-2H198C34%2031%2016%2031%2014%2033m351%2010v6l1%204h9V42h-4l-6%201M12%2063c0%204-7%204%20185%204a7081%207081%200%2000-185-4m353%2044l1%206h9v-11h-10v5m-7%2084l-1%206%201%205h11v-5l-1-7c-2-1-9-1-10%201M24%20254c0%205%200%205%202%205l4-1h2c1%202%204%202%204%200h2c2%202%204%201%204-2%200-2-3-4-4-2h-7c-2%202-2%202-2-1%200-2%200-3-2-3-3%200-3%200-3%204\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/7c778a283cc601e6d43719ebef9fb98d/ca0a1/KySCLMN.webp 223w", "/static/7c778a283cc601e6d43719ebef9fb98d/75680/KySCLMN.webp 445w", "/static/7c778a283cc601e6d43719ebef9fb98d/01940/KySCLMN.webp 548w"],
              "sizes": "(max-width: 548px) 100vw, 548px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/7c778a283cc601e6d43719ebef9fb98d/e92b6/KySCLMN.png 223w", "/static/7c778a283cc601e6d43719ebef9fb98d/e66bf/KySCLMN.png 445w", "/static/7c778a283cc601e6d43719ebef9fb98d/a58fe/KySCLMN.png 548w"],
              "sizes": "(max-width: 548px) 100vw, 548px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/7c778a283cc601e6d43719ebef9fb98d/a58fe/KySCLMN.png",
              "alt": "KySCLMN",
              "title": "KySCLMN",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <ol {...{
      "start": 5
    }}>
      <li parentName="ol"><strong parentName="li">{`Use your design system`}</strong></li>
    </ol>
    <p>{`The best part is that you get to use scales. Scales that you defined in your custom theme.`}</p>
    <p>{`Look at this input definition, for example:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=borderColor%3A%20%22greyVeryLight%22%2C%0Apt%3A%200%2C%0Apr%3A%203%2C%0Apb%3A%200%2C%0Apl%3A%203%2C%0Aheight%3A%205%2C%0Acolor%3A%20%22text%22%2C%0AfontSize%3A%201%2C%0AfontFamily%3A%20%22body%22%2C%0Amb%3A%202%2C"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "334px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "104.03587443946189%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'417\\'%20viewBox=\\'0%200%20400%20417\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M41%2060c-2%202-2%2018-2%20151v148l3%202c4%202%20313%202%20317%200l3-2V211c0-133%200-149-2-151l-1-2H42l-1%202m42%2018c-6%207%203%2016%2010%2010%203-3%203-7%200-10s-7-3-10%200m26-1c-5%202-5%2010%200%2012s11-3%209-8c0-2-5-5-6-5l-3%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9e9c33f2f40eda7eb7f2abc7c2735bf5/ca0a1/techletter.app-screenshot-1593798385841.webp 223w", "/static/9e9c33f2f40eda7eb7f2abc7c2735bf5/7390e/techletter.app-screenshot-1593798385841.webp 334w"],
                "sizes": "(max-width: 334px) 100vw, 334px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9e9c33f2f40eda7eb7f2abc7c2735bf5/e92b6/techletter.app-screenshot-1593798385841.png 223w", "/static/9e9c33f2f40eda7eb7f2abc7c2735bf5/86d36/techletter.app-screenshot-1593798385841.png 334w"],
                "sizes": "(max-width: 334px) 100vw, 334px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/9e9c33f2f40eda7eb7f2abc7c2735bf5/86d36/techletter.app-screenshot-1593798385841.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Lots of magic numbers right? They map to the size and space scales.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=export%20const%20askTia%20%3D%20%7B%0A%20%20%20%20space%3A%20%5B0%2C%204%2C%208%2C%2016%2C%2032%2C%2048%2C%2064%2C%20128%2C%20256%2C%20512%5D%2C%0A%20%20%20%20sizes%3A%20%5B0%2C%204%2C%208%2C%2016%2C%2032%2C%2048%2C%2064%2C%20128%2C%20256%2C%20512%5D%2C"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "518px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "43.04932735426009%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'171\\'%20viewBox=\\'0%200%20400%20171\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M26%2038c-2%202-2%2095%200%2097s346%202%20348%200%202-95%200-97-346-2-348%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/0adbf9a2bed29180291e1b37e95b9c13/ca0a1/techletter.app-screenshot-1593798387860.webp 223w", "/static/0adbf9a2bed29180291e1b37e95b9c13/75680/techletter.app-screenshot-1593798387860.webp 445w", "/static/0adbf9a2bed29180291e1b37e95b9c13/c29d2/techletter.app-screenshot-1593798387860.webp 518w"],
                "sizes": "(max-width: 518px) 100vw, 518px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/0adbf9a2bed29180291e1b37e95b9c13/e92b6/techletter.app-screenshot-1593798387860.png 223w", "/static/0adbf9a2bed29180291e1b37e95b9c13/e66bf/techletter.app-screenshot-1593798387860.png 445w", "/static/0adbf9a2bed29180291e1b37e95b9c13/6b9fd/techletter.app-screenshot-1593798387860.png 518w"],
                "sizes": "(max-width: 518px) 100vw, 518px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/0adbf9a2bed29180291e1b37e95b9c13/6b9fd/techletter.app-screenshot-1593798387860.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p><inlineCode parentName="p">{`pr: 3`}</inlineCode>{` means `}<inlineCode parentName="p">{`padding-right: askTia.space[3]`}</inlineCode>{` 👉 16px`}</p>
    <p>{`Same for color scales. You say `}<inlineCode parentName="p">{`greyVeryLight`}</inlineCode>{` and theme-ui checks the color map to get the digits.`}</p>
    <p>{`And if you need to do something custom, there's the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20div%0A%2F%2F%20padding%2016px%0A%2F%2F%20margin%2032px%0A%2F%2F%20width%20256px%0A%2F%2F%20height%20128px%0A%2F%2F%20background%20%23EF308D%0A%3CBox%20sx%3D%7B%20p%3A%203%2C%20m%3A%204%2C%20width%3A%208%2C%20height%3A%207%20%7D%20bg%3D%22pink%22%20%2F%3E"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "550px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "53.36322869955157%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'214\\'%20viewBox=\\'0%200%20400%20214\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M24%2037c-2%203-1%20141%201%20142%202%203%20348%203%20350%200%202-1%203-139%201-142S26%2034%2024%2037\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1372110170bf188a3ce0e020b1f5cb0b/ca0a1/techletter.app-screenshot-1593798385719.webp 223w", "/static/1372110170bf188a3ce0e020b1f5cb0b/75680/techletter.app-screenshot-1593798385719.webp 445w", "/static/1372110170bf188a3ce0e020b1f5cb0b/12b65/techletter.app-screenshot-1593798385719.webp 550w"],
                "sizes": "(max-width: 550px) 100vw, 550px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1372110170bf188a3ce0e020b1f5cb0b/e92b6/techletter.app-screenshot-1593798385719.png 223w", "/static/1372110170bf188a3ce0e020b1f5cb0b/e66bf/techletter.app-screenshot-1593798385719.png 445w", "/static/1372110170bf188a3ce0e020b1f5cb0b/dd45a/techletter.app-screenshot-1593798385719.png 550w"],
                "sizes": "(max-width: 550px) 100vw, 550px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/1372110170bf188a3ce0e020b1f5cb0b/dd45a/techletter.app-screenshot-1593798385719.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Some elements come with helper props. `}<inlineCode parentName="p">{`bg`}</inlineCode>{` is very common, `}<inlineCode parentName="p">{`variant`}</inlineCode>{` is another.`}</p>
    <p>{`Beyond that, the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop lets you define everything you'd otherwise use the `}<inlineCode parentName="p">{`style`}</inlineCode>{` prop for. Except it goes through your theme config.`}</p>
    <p>{`Also great for adding dark mode. Change theme. 🤘`}</p>
    <p>{`Happy Friday`}</p>
    <p>{`Cheers,`}<br parentName="p"></br>{`
`}{`~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      